import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { imageCandidates } from '../utils/ipfs';

/**
 * Token icon with on-error letter fallback and on-hover 300x300
 * preview portal. Mirrors the front-page TokenImage UX so trending,
 * watchlist, and token-detail surfaces all show the same hover-zoom
 * behaviour.
 *
 * Pass either a direct `src` (Birdeye logo_uri, Pump CDN URL, etc.) or
 * a metadata `uriMetadata` object — uriMetadata.image wins when both
 * are provided. Sizes are tailwind classes so a parent can pass any
 * standard rounded-image size: 'w-7 h-7', 'w-12 h-12', 'w-14 h-14'.
 */
export const TokenIcon: React.FC<{
  src?: string | null;
  uriMetadata?: { image?: string } | null;
  symbol: string;
  name?: string;
  /** Tailwind size classes, e.g. 'w-7 h-7'. Default 'w-12 h-12'. */
  sizeClass?: string;
  /** Extra classes passed through to the img element. */
  className?: string;
  /** Whether the hover-preview portal is shown. Default true. */
  hoverPreview?: boolean;
}> = ({ src, uriMetadata, symbol, name = '', sizeClass = 'w-12 h-12', className = '', hoverPreview = true }) => {
  // Resolve initial source: explicit `src` wins, then uriMetadata.image.
  const [candidates, setCandidates] = useState<string[]>(() => imageCandidates(src || uriMetadata?.image));
  const [idx, setIdx] = useState(0);
  const imageSrc = candidates[idx] ?? null;
  const errored = idx >= candidates.length;
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset on src change (e.g. table row recycles).
  useEffect(() => {
    setCandidates(imageCandidates(src || uriMetadata?.image));
    setIdx(0);
  }, [src, uriMetadata]);

  const showFallback = !imageSrc || errored;
  const canPreview = hoverPreview && !!imageSrc && !errored;
  const initial = (symbol || '?').charAt(0).toUpperCase();

  if (showFallback) {
    // Letter-circle div with readable text scaled by the size class.
    // Avoids the previous "broken image hidden" bug AND the
    // base64-SVG fallback that rendered as a near-invisible dark dot
    // at small sizes.
    return (
      <div
        className={`${sizeClass} rounded-full bg-gray-700 flex items-center justify-center text-gray-300 font-semibold ${className}`}
        title={name || symbol}
      >
        {initial}
      </div>
    );
  }

  return (
    <>
      <img
        ref={imgRef}
        className={`${sizeClass} rounded-full bg-crypto-gray object-cover ${className}`}
        src={imageSrc!}
        alt={name ? `${name} logo` : `${symbol} logo`}
        onError={() => setIdx(i => i + 1)} // next IPFS gateway, then the letter fallback
        onMouseEnter={() => {
          if (!canPreview || !imgRef.current) return;
          const r = imgRef.current.getBoundingClientRect();
          setPreviewPos({ x: r.left, y: r.top });
        }}
        onMouseLeave={() => setPreviewPos(null)}
      />
      {previewPos && canPreview && ReactDOM.createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: previewPos.x, top: previewPos.y, transform: 'translate(-10px, -10px)' }}
        >
          <img
            className="w-[300px] h-[300px] rounded-xl border-2 border-gray-600 shadow-2xl object-cover bg-crypto-gray"
            src={imageSrc!}
            alt={name ? `${name} preview` : `${symbol} preview`}
          />
        </div>,
        document.body,
      )}
    </>
  );
};
